// ════════════════════════════════════════════════════════════════
// DASHBOARD — Main content renderer (config-driven views)
// ════════════════════════════════════════════════════════════════
// Renders different views based on the active widget's type:
// - 'list': CRUD list view for collections/items/identifiers/ledgerEntries
// - 'summary': Portfolio allocation chart + totals
// - 'history': Transaction timeline
// - 'settings': App settings, export/import, security
// ════════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/database';
import { decrypt } from '../db/crypto';
import type { AppConfig, WidgetDefinition } from '../config/app.config';
import type { Collection, Item, Identifier, LedgerEntry } from '../types';
import { DynamicForm } from './Forms';
import { QRCodeDisplay } from './QR';
import {
  createCollection, updateCollection, deleteCollection,
  createItem, updateItem, deleteItem,
  createIdentifier, deleteIdentifier,
  createLedgerEntry, deleteLedgerEntry,
  exportEncryptedBackup, importEncryptedBackup,
} from '../db/repositories';
import { useLicenseStore } from '../stores/licenseStore';
import { canAddCollection, canAddItem, canAddIdentifier, getRemainingCollections, getRemainingItems } from '../utils/limits';

interface DashboardProps {
  config: AppConfig;
  widget: WidgetDefinition;
}

export function Dashboard({ config, widget }: DashboardProps) {
  switch (widget.type) {
    case 'list':
      return <ListView config={config} widget={widget} />;
    case 'summary':
      return <SummaryView config={config} />;
    case 'history':
      return <HistoryView config={config} />;
    case 'settings':
      return <SettingsView config={config} />;
    case 'canvas':
      return <div className="text-vault-500">Canvas mode active</div>;
    default:
      return <div className="text-vault-500">Unknown view type</div>;
  }
}

// ════════════════════════════════════════════════════════════════
// LIST VIEW — Renders collections, their items, and nested data
// ════════════════════════════════════════════════════════════════

function ListView({ config, widget }: { config: AppConfig; widget: WidgetDefinition }) {
  const [selectedCollection, setSelectedCollection] = useState<Collection | null>(null);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingEntity, setEditingEntity] = useState<'collection' | 'item' | 'identifier' | 'ledgerEntry' | null>(null);
  const [editingData, setEditingData] = useState<Record<string, unknown> | null>(null);
  const [showQRFor, setShowQRFor] = useState<string | null>(null);
  const [decryptedAddress, setDecryptedAddress] = useState<string>('');

  const collections = useLiveQuery(() => db.collections.orderBy('createdAt').reverse().toArray(), [], []);
  const items = useLiveQuery(
    async () => selectedCollection ? db.items.where('collectionId').equals(selectedCollection.id).toArray() : [] as Item[],
    [selectedCollection?.id],
    [] as Item[]
  );
  const identifiers = useLiveQuery(
    async () => selectedItem ? db.identifiers.where('itemId').equals(selectedItem.id).toArray() : [] as Identifier[],
    [selectedItem?.id],
    [] as Identifier[]
  );
  const ledgerEntries = useLiveQuery(
    async () => selectedCollection ? db.ledgerEntries.where('collectionId').equals(selectedCollection.id).reverse().sortBy('date') : [] as LedgerEntry[],
    [selectedCollection?.id],
    [] as LedgerEntry[]
  );

  const isPro = useLicenseStore((s) => s.isPro);
  const entityDef = widget.entityId ? config.entities[widget.entityId] : config.entities.collection;

  // ── Collection List ──
  if (!selectedCollection) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-vault-100">{entityDef.plural}</h2>
          {canAddCollection(collections.length) ? (
            <button
              onClick={() => { setEditingEntity('collection'); setEditingData(null); setShowForm(true); }}
              className="vault-button-primary text-sm"
            >
              + Add {entityDef.singular}
            </button>
          ) : (
            <span className="text-xs text-vault-500">
              Free limit reached ({getRemainingCollections(collections.length)} remaining)
            </span>
          )}
        </div>

        {collections.length === 0 ? (
          <EmptyState label={entityDef.singular} />
        ) : (
          <div className="grid gap-3">
            {collections.map((col) => (
              <button
                key={col.id}
                onClick={() => setSelectedCollection(col)}
                className="vault-card text-left hover:border-accent-500/30 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium text-vault-100">{col.name}</h3>
                    <p className="text-sm text-vault-500">{col.category} {col.manufacturer ? `· ${col.manufacturer}` : ''}</p>
                  </div>
                  <span className="vault-badge bg-vault-800 text-vault-400">
                    {items.filter(i => i.collectionId === col.id).length} assets
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}

        {showForm && editingEntity === 'collection' && (
          <Modal title={editingData ? `Edit ${entityDef.singular}` : `Add ${entityDef.singular}`} onClose={() => setShowForm(false)}>
            <DynamicForm
              fields={entityDef.fields}
              title={editingData ? `Edit ${entityDef.singular}` : `Add ${entityDef.singular}`}
              initialData={editingData as Record<string, string | number | boolean | undefined>}
              seedPhraseGuard={config.security.seedPhraseGuard}
              qrScanField="serialNumberEncrypted"
              onSubmit={async (data) => {
                if (editingData) {
                  await updateCollection((editingData as unknown as Collection).id, data, entityDef.fields);
                } else {
                  await createCollection(data, entityDef.fields);
                }
                setShowForm(false);
                setEditingData(null);
              }}
              onCancel={() => setShowForm(false)}
            />
          </Modal>
        )}
      </div>
    );
  }

  // ── Collection Detail (Items + Addresses + History) ──
  return (
    <div className="space-y-6">
      {/* Back button + header */}
      <div>
        <button
          onClick={() => { setSelectedCollection(null); setSelectedItem(null); }}
          className="vault-button-ghost text-sm mb-2"
        >
          ← Back to {entityDef.plural}
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-vault-100">{selectedCollection.name}</h2>
            <p className="text-sm text-vault-500">
              {selectedCollection.category} {selectedCollection.manufacturer ? `· ${selectedCollection.manufacturer}` : ''}
              {selectedCollection.firmwareVersion ? ` · FW ${selectedCollection.firmwareVersion}` : ''}
            </p>
          </div>
          <button
            onClick={() => { setEditingEntity('collection'); setEditingData(selectedCollection as unknown as Record<string, unknown>); setShowForm(true); }}
            className="vault-button-ghost text-sm"
          >
            Edit
          </button>
        </div>
      </div>

      {/* Items section */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-vault-400 uppercase tracking-wide">
            {config.entities.item.plural}
          </h3>
          {canAddItem(items.length) ? (
            <button
              onClick={() => { setEditingEntity('item'); setEditingData(null); setShowForm(true); }}
              className="vault-button-secondary text-xs"
            >
              + Add {config.entities.item.singular}
            </button>
          ) : (
            <span className="text-xs text-vault-500">Limit reached</span>
          )}
        </div>

        {items.length === 0 ? (
          <p className="text-sm text-vault-600 py-4">No assets yet.</p>
        ) : (
          <div className="grid gap-2">
            {items.map((item) => (
              <button
                key={item.id}
                onClick={() => setSelectedItem(item)}
                className="vault-card text-left hover:border-accent-500/30 transition-colors !p-3"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent-500/10 flex items-center justify-center text-accent-400 font-bold text-sm">
                      {item.symbol.slice(0, 3)}
                    </div>
                    <div>
                      <div className="font-medium text-vault-100 text-sm">{item.name}</div>
                      <div className="text-xs text-vault-500">{item.balance} {item.symbol}</div>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Selected Item: Addresses + Transactions */}
      {selectedItem && (
        <div className="space-y-4 border-t border-vault-800 pt-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-vault-400 uppercase tracking-wide">
              {selectedItem.name} — {config.entities.identifier.plural}
            </h3>
            <div className="flex gap-2">
              {canAddIdentifier(identifiers.length) ? (
                <button
                  onClick={() => { setEditingEntity('identifier'); setEditingData(null); setShowForm(true); }}
                  className="vault-button-secondary text-xs"
                >
                  + Add {config.entities.identifier.singular}
                </button>
              ) : (
                <span className="text-xs text-vault-500">Limit reached</span>
              )}
              <button
                onClick={() => { setSelectedItem(null); }}
                className="vault-button-ghost text-xs"
              >
                Close
              </button>
            </div>
          </div>

          {identifiers.length === 0 ? (
            <p className="text-sm text-vault-600 py-2">No addresses yet.</p>
          ) : (
            <div className="grid gap-2">
              {identifiers.map((addr) => (
                <div key={addr.id} className="vault-card !p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-vault-100 text-sm">{addr.label}</span>
                        {addr.isPrimary && <span className="vault-badge bg-accent-500/20 text-accent-400">Primary</span>}
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            const dec = await decrypt(addr.valueEncrypted);
                            setDecryptedAddress(dec);
                            setShowQRFor(addr.id);
                          } catch { /* locked */ }
                        }}
                        className="text-xs text-accent-400 hover:underline mt-1"
                      >
                        {showQRFor === addr.id ? decryptedAddress.slice(0, 20) + '...' : 'Show address & QR'}
                      </button>
                    </div>
                    <button
                      onClick={() => deleteIdentifier(addr.id)}
                      className="vault-button-ghost !p-1.5 text-red-400/60"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    </button>
                  </div>
                  {showQRFor === addr.id && decryptedAddress && (
                    <div className="mt-3 flex flex-col items-center gap-2">
                      <QRCodeDisplay value={decryptedAddress} size={200} />
                      <button
                        onClick={() => { setShowQRFor(null); setDecryptedAddress(''); }}
                        className="vault-button-ghost text-xs"
                      >
                        Hide
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Ledger Entries */}
      <div className="border-t border-vault-800 pt-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-vault-400 uppercase tracking-wide">
            {config.entities.ledgerEntry.plural}
          </h3>
          <button
            onClick={() => { setEditingEntity('ledgerEntry'); setEditingData(null); setShowForm(true); }}
            className="vault-button-secondary text-xs"
          >
            + Add {config.entities.ledgerEntry.singular}
          </button>
        </div>

        {ledgerEntries.length === 0 ? (
          <p className="text-sm text-vault-600 py-2">No transactions yet.</p>
        ) : (
          <div className="space-y-1">
            {ledgerEntries.slice(0, 10).map((entry) => (
              <div key={entry.id} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-vault-900/50">
                <div className="flex items-center gap-3">
                  <span className={`vault-badge ${
                    entry.entryType === 'purchase' ? 'bg-accent-500/20 text-accent-400' :
                    entry.entryType === 'transfer_in' ? 'bg-green-500/20 text-green-400' :
                    entry.entryType === 'transfer_out' ? 'bg-orange-500/20 text-orange-400' :
                    'bg-vault-800 text-vault-400'
                  }`}>
                    {entry.entryType}
                  </span>
                  <span className="text-sm text-vault-300">{entry.amount}</span>
                  {entry.source && <span className="text-xs text-vault-600">from {entry.source}</span>}
                </div>
                <button
                  onClick={() => deleteLedgerEntry(entry.id)}
                  className="vault-button-ghost !p-1 text-vault-600"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete collection */}
      <div className="border-t border-vault-800 pt-4">
        <button
          onClick={async () => {
            await deleteCollection(selectedCollection.id);
            setSelectedCollection(null);
          }}
          className="vault-button-ghost text-sm text-red-400/60"
        >
          Delete {entityDef.singular.toLowerCase()}
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <Modal title={editingData ? 'Edit' : 'Add'} onClose={() => { setShowForm(false); setEditingData(null); }}>
          {editingEntity === 'collection' && (
            <DynamicForm
              fields={config.entities.collection.fields}
              title={editingData ? 'Edit Wallet' : 'Add Wallet'}
              initialData={editingData as Record<string, string | number | boolean | undefined>}
              seedPhraseGuard={config.security.seedPhraseGuard}
              qrScanField="serialNumberEncrypted"
              onSubmit={async (data) => {
                if (editingData) {
                  await updateCollection((editingData as unknown as Collection).id, data, config.entities.collection.fields);
                } else {
                  await createCollection(data, config.entities.collection.fields);
                }
                setShowForm(false);
                setEditingData(null);
              }}
              onCancel={() => { setShowForm(false); setEditingData(null); }}
            />
          )}
          {editingEntity === 'item' && (
            <DynamicForm
              fields={config.entities.item.fields}
              title="Add Asset"
              seedPhraseGuard={config.security.seedPhraseGuard}
              onSubmit={async (data) => {
                await createItem(selectedCollection.id, data, config.entities.item.fields);
                setShowForm(false);
              }}
              onCancel={() => setShowForm(false)}
            />
          )}
          {editingEntity === 'identifier' && (
            <DynamicForm
              fields={config.entities.identifier.fields}
              title="Add Address"
              seedPhraseGuard={config.security.seedPhraseGuard}
              qrScanField="valueEncrypted"
              onSubmit={async (data) => {
                await createIdentifier(selectedItem!.id, data, config.entities.identifier.fields);
                setShowForm(false);
              }}
              onCancel={() => setShowForm(false)}
            />
          )}
          {editingEntity === 'ledgerEntry' && (
            <DynamicForm
              fields={config.entities.ledgerEntry.fields}
              title="Add Transaction"
              seedPhraseGuard={config.security.seedPhraseGuard}
              onSubmit={async (data) => {
                await createLedgerEntry(selectedCollection.id, data, config.entities.ledgerEntry.fields);
                setShowForm(false);
              }}
              onCancel={() => setShowForm(false)}
            />
          )}
        </Modal>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// SUMMARY VIEW — Portfolio allocation + totals
// ════════════════════════════════════════════════════════════════

function SummaryView({ config }: { config: AppConfig }) {
  const collections = useLiveQuery(() => db.collections.toArray(), [], []);
  const items = useLiveQuery(() => db.items.toArray(), [], []);

  const totalAssets = items.length;
  const totalWallets = collections.length;
  const uniqueSymbols = new Set(items.map((i) => i.symbol)).size;

  // Calculate allocation by symbol
  const allocation: Record<string, number> = {};
  items.forEach((item) => {
    allocation[item.symbol] = (allocation[item.symbol] || 0) + item.balance;
  });
  const totalBalance = Object.values(allocation).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-vault-100">Portfolio Summary</h2>

      {/* Stats grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatCard label="Wallets" value={totalWallets} />
        <StatCard label="Assets" value={totalAssets} />
        <StatCard label="Symbols" value={uniqueSymbols} />
      </div>

      {/* Allocation list */}
      <div className="vault-card">
        <h3 className="text-sm font-medium text-vault-400 uppercase tracking-wide mb-3">Allocation</h3>
        {totalBalance === 0 ? (
          <p className="text-sm text-vault-600 py-4">No balances recorded yet.</p>
        ) : (
          <div className="space-y-2">
            {Object.entries(allocation).map(([symbol, balance]) => (
              <div key={symbol} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-accent-500/10 flex items-center justify-center text-accent-400 font-bold text-xs">
                    {symbol.slice(0, 3)}
                  </div>
                  <span className="text-sm text-vault-200">{symbol}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-vault-300">{balance}</span>
                  <div className="w-20 h-2 bg-vault-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-accent-500 rounded-full"
                      style={{ width: `${(balance / totalBalance) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Canvas chart hint */}
      {config.canvas.enabled && (
        <div className="vault-card text-center">
          <p className="text-xs text-vault-600">
            Canvas chart engine ready. Set widget type to 'canvas' in config to render
            interactive charts via HTML5 Canvas.
          </p>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// HISTORY VIEW — Transaction timeline
// ════════════════════════════════════════════════════════════════

function HistoryView({ config: _config }: { config: AppConfig }) {
  const entries = useLiveQuery(() => db.ledgerEntries.orderBy('date').reverse().limit(50).toArray(), [], []);

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-vault-100">Transaction History</h2>

      {entries.length === 0 ? (
        <EmptyState label="transactions" />
      ) : (
        <div className="space-y-1">
          {entries.map((entry) => (
            <div key={entry.id} className="vault-card !p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className={`vault-badge ${
                  entry.entryType === 'purchase' ? 'bg-accent-500/20 text-accent-400' :
                  entry.entryType === 'transfer_in' ? 'bg-green-500/20 text-green-400' :
                  entry.entryType === 'transfer_out' ? 'bg-orange-500/20 text-orange-400' :
                  'bg-vault-800 text-vault-400'
                }`}>
                  {entry.entryType}
                </span>
                <div>
                  <div className="text-sm text-vault-200">{entry.amount}</div>
                  {entry.source && <div className="text-xs text-vault-600">{entry.source}</div>}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-vault-500">
                  {new Date(entry.date).toLocaleDateString()}
                </div>
                {entry.totalValue && (
                  <div className="text-xs text-vault-400">${entry.totalValue}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// SETTINGS VIEW — Export, import, security, about
// ════════════════════════════════════════════════════════════════

function SettingsView({ config }: { config: AppConfig }) {
  const [exportStatus, setExportStatus] = useState('');
  const [importStatus, setImportStatus] = useState('');
  const isPro = useLicenseStore((s) => s.isPro);

  const handleExport = async () => {
    try {
      const blob = await exportEncryptedBackup();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vault-backup-${Date.now()}.enc`;
      a.click();
      URL.revokeObjectURL(url);
      setExportStatus('Backup exported successfully.');
    } catch {
      setExportStatus('Export failed. Is the vault unlocked?');
    }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await importEncryptedBackup(file);
      setImportStatus('Backup imported successfully.');
    } catch {
      setImportStatus('Import failed. Check the file and passphrase.');
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-vault-100">Settings</h2>

      {/* Security */}
      <div className="vault-card">
        <h3 className="text-sm font-medium text-vault-400 uppercase tracking-wide mb-3">Security</h3>
        <div className="space-y-2 text-sm text-vault-300">
          <div className="flex justify-between">
            <span>Master Passphrase</span>
            <span className="text-vault-500">Active</span>
          </div>
          <div className="flex justify-between">
            <span>Auto-lock timer</span>
            <span className="text-vault-500">{config.security.autoLockMinutes} min</span>
          </div>
          <div className="flex justify-between">
            <span>Seed phrase guard</span>
            <span className={config.security.seedPhraseGuard ? 'text-green-400' : 'text-vault-500'}>
              {config.security.seedPhraseGuard ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Encryption</span>
            <span className="text-vault-500">AES-256-GCM</span>
          </div>
        </div>
      </div>

      {/* Backup */}
      <div className="vault-card">
        <h3 className="text-sm font-medium text-vault-400 uppercase tracking-wide mb-3">Backup & Restore</h3>
        <div className="space-y-3">
          <button onClick={handleExport} className="vault-button-secondary w-full text-sm">
            Export Encrypted Backup
          </button>
          {exportStatus && <p className="text-xs text-vault-500">{exportStatus}</p>}

          <label className="vault-button-secondary w-full text-sm block text-center cursor-pointer">
            Import Encrypted Backup
            <input type="file" accept=".enc" onChange={handleImport} className="hidden" />
          </label>
          {importStatus && <p className="text-xs text-vault-500">{importStatus}</p>}

          <p className="text-xs text-vault-600">
            Backups are encrypted with your master passphrase. They can only be
            decrypted on a device where you enter the same passphrase.
          </p>
        </div>
      </div>

      {/* License */}
      <div className="vault-card">
        <h3 className="text-sm font-medium text-vault-400 uppercase tracking-wide mb-3">License</h3>
        <div className="flex justify-between items-center">
          <span className="text-sm text-vault-300">{isPro ? 'Pro activated' : 'Free tier'}</span>
          {isPro ? (
            <span className="vault-badge bg-accent-500/20 text-accent-400">PRO</span>
          ) : (
            <span className="vault-badge bg-vault-800 text-vault-400">FREE</span>
          )}
        </div>
      </div>

      {/* About */}
      <div className="vault-card">
        <h3 className="text-sm font-medium text-vault-400 uppercase tracking-wide mb-3">About</h3>
        <div className="space-y-1 text-xs text-vault-500">
          <p>{config.app.name} v1.0.0</p>
          <p>{config.app.description}</p>
          <p className="mt-2 pt-2 border-t border-vault-800">
            This app is NOT a cryptocurrency wallet. It does not store private keys,
            connect to blockchains, or facilitate transactions. All data is stored
            locally in your browser's encrypted IndexedDB.
          </p>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// SHARED COMPONENTS
// ════════════════════════════════════════════════════════════════

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="vault-card text-center">
      <div className="text-2xl font-bold text-accent-400">{value}</div>
      <div className="text-xs text-vault-500 mt-1">{label}</div>
    </div>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="vault-card text-center py-8">
      <p className="text-vault-500 text-sm">No {label} yet. Add one to get started.</p>
    </div>
  );
}

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-vault-900 border border-vault-800 rounded-2xl p-6 max-w-md w-full my-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-vault-100">{title}</h2>
          <button onClick={onClose} className="vault-button-ghost !p-2">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
